import { z } from 'zod'
import { isLinkedInOrXUrl } from './market-maker/claim'

export const MAX_UTR_CLAIM_DOCUMENT_BYTES = 10 * 1024 * 1024

export const UTR_CLAIM_DOCUMENT_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  'text/markdown',
] as const

/** All uploaded claim documents live under this R2 key prefix. */
export const UTR_CLAIM_DOCUMENT_KEY_PREFIX = 'utr-claims/'

export const UtrClaimDocumentSchema = z
  .object({
    key: z.string().min(1).max(500).startsWith(UTR_CLAIM_DOCUMENT_KEY_PREFIX),
    fileName: z.string().min(1).max(200),
    mimeType: z.enum(UTR_CLAIM_DOCUMENT_MIME_TYPES),
    sizeBytes: z.number().int().positive().max(MAX_UTR_CLAIM_DOCUMENT_BYTES),
    // HMAC receipt from the upload route; verified on submit, never persisted.
    // Optional: absent when turnstile is disabled and on persisted documents.
    signature: z.string().min(1).max(200).optional(),
  })
  .strict()
export type UtrClaimDocument = z.infer<typeof UtrClaimDocumentSchema>

const shortText = (max = 200) => z.string().trim().min(1).max(max)
const percent = z.number().min(0).max(100)
// Free-form in the admin disclosure editor only — the public claim form stays
// strictly numeric. Numbers stay valid so rows stored before the change parse.
const percentOrText = z.union([percent, shortText(120)])
const thresholdCount = z.number().int().min(1).max(1000)
const thresholdCountOrText = z.union([thresholdCount, shortText(120)])

const httpUrl = z
  .string()
  .trim()
  .url()
  .max(500)
  .refine((value) => /^https?:\/\//i.test(value), 'Must be an HTTP(S) URL')

export const UtrClaimInsiderIdentitySchema = z
  .object({
    name: shortText(),
    role: shortText(120),
    profileUrl: httpUrl,
  })
  .strict()
export type UtrClaimInsiderIdentity = z.infer<typeof UtrClaimInsiderIdentitySchema>

// description is admin-supplied; optional so claim-shaped data stays valid
export const UtrDisclosureInsiderIdentitySchema = UtrClaimInsiderIdentitySchema.extend({
  description: shortText(500).optional(),
})
export type UtrDisclosureInsiderIdentity = z.infer<typeof UtrDisclosureInsiderIdentitySchema>

export const UtrClaimOrgStructureEntrySchema = z
  .object({
    companyName: shortText(),
    jurisdiction: shortText(120),
    description: shortText(500),
  })
  .strict()
export type UtrClaimOrgStructureEntry = z.infer<typeof UtrClaimOrgStructureEntrySchema>

export const UtrClaimInsiderWalletSchema = z
  .object({
    walletAddress: shortText(120),
    allocationPct: percent,
    ownerType: shortText(120),
  })
  .strict()
export type UtrClaimInsiderWallet = z.infer<typeof UtrClaimInsiderWalletSchema>

export const UtrDisclosureInsiderWalletSchema = UtrClaimInsiderWalletSchema.extend({
  allocationPct: percentOrText,
})
export type UtrDisclosureInsiderWallet = z.infer<typeof UtrDisclosureInsiderWalletSchema>

export const UTR_CLAIM_UNLOCK_FREQUENCIES = ['Daily', 'Monthly'] as const
export type UtrClaimUnlockFrequency = (typeof UTR_CLAIM_UNLOCK_FREQUENCIES)[number]

/** One tokenomics group carrying both its distribution and vesting terms, rendered as two linked tables. */
export const UtrDisclosureTokenomicsGroupSchema = z
  .object({
    groupName: shortText(),
    allocationPct: percent,
    groupCategory: shortText(120),
    tgeUnlockPct: percent,
    lockUpDurationMonths: z.number().int().min(0).max(600),
    cliffUnlockPct: percent,
    unlockDurationMonths: z.number().int().min(0).max(600),
    unlockFrequency: z.enum(UTR_CLAIM_UNLOCK_FREQUENCIES),
  })
  .strict()
export type UtrDisclosureTokenomicsGroup = z.infer<typeof UtrDisclosureTokenomicsGroupSchema>

export const UtrClaimExternalAllocationSchema = z
  .object({
    counterpartyName: shortText(),
    allocationPct: percent,
    categorization: shortText(120),
  })
  .strict()
export type UtrClaimExternalAllocation = z.infer<typeof UtrClaimExternalAllocationSchema>

export const UtrDisclosureExternalAllocationSchema = UtrClaimExternalAllocationSchema.extend({
  allocationPct: percentOrText,
})
export type UtrDisclosureExternalAllocation = z.infer<typeof UtrDisclosureExternalAllocationSchema>

const multisigEntryBase = z
  .object({
    walletAddress: shortText(120),
    thresholdRequired: thresholdCount,
    thresholdTotal: thresholdCount,
    signerNames: shortText(1000),
  })
  .strict()

export const UtrClaimMultisigEntrySchema = multisigEntryBase.refine(
  (entry) => entry.thresholdRequired <= entry.thresholdTotal,
  {
    message: 'Required signatures cannot exceed the total number of signers',
    path: ['thresholdRequired'],
  },
)
export type UtrClaimMultisigEntry = z.infer<typeof UtrClaimMultisigEntrySchema>

export const UtrDisclosureMultisigEntrySchema = multisigEntryBase
  .extend({
    thresholdRequired: thresholdCountOrText,
    thresholdTotal: thresholdCountOrText,
  })
  .refine(
    (entry) =>
      typeof entry.thresholdRequired !== 'number' ||
      typeof entry.thresholdTotal !== 'number' ||
      entry.thresholdRequired <= entry.thresholdTotal,
    {
      message: 'Required signatures cannot exceed the total number of signers',
      path: ['thresholdRequired'],
    },
  )
export type UtrDisclosureMultisigEntry = z.infer<typeof UtrDisclosureMultisigEntrySchema>

export const UtrClaimLicenseEntrySchema = z
  .object({
    name: shortText(),
    link: httpUrl.optional(),
    description: shortText(500),
  })
  .strict()
export type UtrClaimLicenseEntry = z.infer<typeof UtrClaimLicenseEntrySchema>

export const UtrClaimMarketMakerEngagementSchema = z
  .object({
    // id from the /market-maker-rfq/market-makers search select
    marketMakerId: z.string().min(1).max(100),
    marketMakerName: shortText(),
    allocationPct: percent,
    engagementType: shortText(120),
  })
  .strict()
export type UtrClaimMarketMakerEngagement = z.infer<typeof UtrClaimMarketMakerEngagementSchema>

export const UtrDisclosureMarketMakerEngagementSchema = UtrClaimMarketMakerEngagementSchema.extend({
  allocationPct: percentOrText,
  marketMakerLogoUrl: z.string().nullable().optional(),
})
export type UtrDisclosureMarketMakerEngagement = z.infer<typeof UtrDisclosureMarketMakerEngagementSchema>

export const UtrClaimExchangeEngagementSchema = z
  .object({
    // id from the /market-maker-rfq/exchanges search select
    exchangeId: z.string().min(1).max(100),
    exchangeName: shortText(),
    commercialCommitment: z.boolean(),
    details: z.string().trim().max(500).optional(),
  })
  .strict()
export type UtrClaimExchangeEngagement = z.infer<typeof UtrClaimExchangeEngagementSchema>

/** Commentary is supplied by Forgd admins and replaces claim details on the public token profile. */
export const UtrDisclosureExchangeEngagementSchema = UtrClaimExchangeEngagementSchema.extend({
  commentary: z.string().trim().max(500).optional(),
  exchangeLogoUrl: z.string().nullable().optional(),
})
export type UtrDisclosureExchangeEngagement = z.infer<typeof UtrDisclosureExchangeEngagementSchema>

export const UTR_CLAIM_AUDIT_TYPES = ['Technical', 'Financial'] as const
export type UtrClaimAuditType = (typeof UTR_CLAIM_AUDIT_TYPES)[number]

export const UtrClaimAuditorSchema = z
  .object({
    name: shortText(),
    // free text per the claim-form design (e.g. "Technical", "Financial")
    type: shortText(120),
    link: httpUrl.optional(),
  })
  .strict()
export type UtrClaimAuditor = z.infer<typeof UtrClaimAuditorSchema>

const MAX_SECTION_ROWS = 100

export const UtrClaimDisclosuresSchema = z
  .object({
    insiderIdentities: z.array(UtrClaimInsiderIdentitySchema).max(MAX_SECTION_ROWS),
    organizationalStructure: z.array(UtrClaimOrgStructureEntrySchema).max(MAX_SECTION_ROWS),
    insiderWallets: z.array(UtrClaimInsiderWalletSchema).max(MAX_SECTION_ROWS),
    tokenomicsGroups: z.array(UtrDisclosureTokenomicsGroupSchema).max(MAX_SECTION_ROWS),
    externalAllocations: z.array(UtrClaimExternalAllocationSchema).max(MAX_SECTION_ROWS),
    multisigWallets: z.array(UtrClaimMultisigEntrySchema).max(MAX_SECTION_ROWS),
    licenses: z.array(UtrClaimLicenseEntrySchema).max(MAX_SECTION_ROWS),
    marketMakerEngagements: z.array(UtrClaimMarketMakerEngagementSchema).max(MAX_SECTION_ROWS),
    // the "willing to verify these engagements via Market Maker Monitoring?" question
    verifyMmEngagements: z.boolean(),
    exchangeEngagements: z.array(UtrClaimExchangeEngagementSchema).max(MAX_SECTION_ROWS),
    auditors: z.array(UtrClaimAuditorSchema).max(MAX_SECTION_ROWS),
  })
  .strict()
export type UtrClaimDisclosures = z.infer<typeof UtrClaimDisclosuresSchema>

export const UtrPreTgePerformanceCommentarySchema = z
  .object({
    fundraising_total: z.string().trim().max(5000).optional(),
    investor_quality: z.string().trim().max(5000).optional(),
    roadmap_on_time: z.string().trim().max(5000).optional(),
    core_software_open: z.string().trim().max(5000).optional(),
    audits_public: z.string().trim().max(5000).optional(),
    revenue_generated: z.string().trim().max(5000).optional(),
    community_interest_distribution: z.string().trim().max(5000).optional(),
    community_retention: z.string().trim().max(5000).optional(),
  })
  .strict()
export type UtrPreTgePerformanceCommentary = z.infer<typeof UtrPreTgePerformanceCommentarySchema>

/** Canonical disclosure data rendered on a UTR token profile. */
export const UtrDisclosureDataSchema = UtrClaimDisclosuresSchema.extend({
  insiderIdentities: z.array(UtrDisclosureInsiderIdentitySchema).max(MAX_SECTION_ROWS),
  insiderWallets: z.array(UtrDisclosureInsiderWalletSchema).max(MAX_SECTION_ROWS),
  externalAllocations: z.array(UtrDisclosureExternalAllocationSchema).max(MAX_SECTION_ROWS),
  multisigWallets: z.array(UtrDisclosureMultisigEntrySchema).max(MAX_SECTION_ROWS),
  marketMakerEngagements: z.array(UtrDisclosureMarketMakerEngagementSchema).max(MAX_SECTION_ROWS),
  exchangeEngagements: z.array(UtrDisclosureExchangeEngagementSchema).max(MAX_SECTION_ROWS),
  roleDescriptionsCommentary: z.string().trim().max(5000).optional(),
  vestingActualCommentary: z.string().trim().max(5000).optional(),
  treasuryReportingCommentary: z.string().trim().max(5000).optional(),
  cashFlowCommentary: z.string().trim().max(5000).optional(),
  preTgePerformanceCommentary: UtrPreTgePerformanceCommentarySchema.optional(),
})
export type UtrDisclosureData = z.infer<typeof UtrDisclosureDataSchema>

export const UtrDisclosureResponseSchema = z
  .object({
    arcticId: z.string().uuid(),
    disclosures: UtrDisclosureDataSchema.nullable(),
    updatedAt: z.string().datetime().nullable(),
    isOwner: z.boolean(),
    /** The owner's Forgd project — only exposed to the owner; null for other viewers and legacy link-less rows. */
    ownerProjectId: z.string().uuid().nullable(),
  })
  .strict()
export type UtrDisclosureResponse = z.infer<typeof UtrDisclosureResponseSchema>

export const UtrDisclosureServiceResponseSchema = UtrDisclosureResponseSchema.omit({
  isOwner: true,
  ownerProjectId: true,
})

export const MAX_UTR_CLAIM_SECTION_DOCUMENTS = 10
const documentList = z.array(UtrClaimDocumentSchema).max(MAX_UTR_CLAIM_SECTION_DOCUMENTS)

/** One list per "Attach Documents" block on the form. */
export const UtrClaimDocumentsSchema = z
  .object({
    alignment: documentList,
    teamOrg: documentList,
    tokenomics: documentList,
    treasuryCashFlows: documentList,
    legal: documentList,
    serviceProviders: documentList,
  })
  .strict()
export type UtrClaimDocuments = z.infer<typeof UtrClaimDocumentsSchema>

// verified = the user owns the verified profile (gets the editor); listed = in the
// UTR but not verified for this user; unlisted = not in the UTR
export const UtrTokenSettingsStateSchema = z.enum(['verified', 'listed', 'unlisted'])
export type UtrTokenSettingsState = z.infer<typeof UtrTokenSettingsStateSchema>

export const UtrTokenSettingsResponseSchema = z
  .object({
    state: UtrTokenSettingsStateSchema,
    arcticId: z.string().uuid().nullable(),
    disclosures: UtrDisclosureDataSchema.nullable(),
    documents: UtrClaimDocumentsSchema.nullable(),
    updatedAt: z.string().datetime().nullable(),
  })
  .strict()
export type UtrTokenSettingsResponse = z.infer<typeof UtrTokenSettingsResponseSchema>

export const UtrTokenSettingsMarketMakerEngagementSchema = UtrClaimMarketMakerEngagementSchema.extend({
  allocationPct: percentOrText,
})
export type UtrTokenSettingsMarketMakerEngagement = z.infer<typeof UtrTokenSettingsMarketMakerEngagementSchema>

// the claim shape plus admin free-text values, so an owner save carries them through;
// admin-only fields stay excluded and are re-attached server-side
export const UtrTokenSettingsDisclosuresSchema = UtrClaimDisclosuresSchema.extend({
  insiderWallets: z.array(UtrDisclosureInsiderWalletSchema).max(MAX_SECTION_ROWS),
  externalAllocations: z.array(UtrDisclosureExternalAllocationSchema).max(MAX_SECTION_ROWS),
  multisigWallets: z.array(UtrDisclosureMultisigEntrySchema).max(MAX_SECTION_ROWS),
  marketMakerEngagements: z.array(UtrTokenSettingsMarketMakerEngagementSchema).max(MAX_SECTION_ROWS),
})
export type UtrTokenSettingsDisclosures = z.infer<typeof UtrTokenSettingsDisclosuresSchema>

export const UtrTokenSettingsUpdateBodySchema = z
  .object({
    projectId: z.string().uuid(),
    disclosures: UtrTokenSettingsDisclosuresSchema,
    documents: UtrClaimDocumentsSchema,
  })
  .strict()
export type UtrTokenSettingsUpdateBody = z.infer<typeof UtrTokenSettingsUpdateBodySchema>

export const UtrClaimRequestBodySchema = z
  .object({
    coingeckoId: z.string().trim().min(1).max(100).nullable(),
    tokenName: shortText(),
    tokenTicker: z.string().trim().max(30).optional(),
    firstName: shortText(100),
    lastName: shortText(100),
    position: shortText(200),
    socialProfileUrl: httpUrl.refine(isLinkedInOrXUrl, 'Must be a LinkedIn or X profile URL').optional(),
    email: z.string().email().max(320),
    telegram: z.string().trim().max(100).optional(),
    disclosures: UtrClaimDisclosuresSchema,
    documents: UtrClaimDocumentsSchema,
    alignmentConsent: z.literal(true),
    termsConsent: z.literal(true),
    turnstileToken: z.string().optional(),
  })
  .strict()
export type UtrClaimRequestBody = z.infer<typeof UtrClaimRequestBodySchema>
